import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Send, Loader2, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

function Bar({ label, used, cap, testId, children }) {
  const pct = cap ? Math.min(100, Math.round((100 * used) / cap)) : 0;
  return (
    <div className="rounded-xl border border-white/10 bg-[#041A3B] p-4 space-y-2" data-testid={testId}>
      <div className="flex justify-between text-sm">
        <span className="text-white">{label}</span>
        <span className="text-[#A0B4CC] font-mono">{used} / {cap} today</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full bg-[#00E5FF] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs text-[#5C7599]">{children}</div>
    </div>
  );
}

export function DailyBatchesPanel() {
  const { api } = useAuth();
  const [b, setB] = useState(null);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => api.get('/api/admin/email/batches').then(({ data }) => setB(data)).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  if (!b) return null;
  const inv = b.invitations;
  const rem = b.reminders;
  const fp = rem.followups_pending || {};

  const sendNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/api/admin/email/invitations/run');
      toast.success(`Sent ${data.sent} invitation(s)${data.daily_cap_reached ? ' · daily limit reached, continues tomorrow' : ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Run failed'); }
    setRunning(false);
  };

  return (
    <div className="mb-6 space-y-2" data-testid="daily-batches-panel">
      <p className="text-xs uppercase tracking-wider text-[#5C7599] flex items-center gap-2">
        <CalendarClock className="w-4 h-4 text-[#00E5FF]" />Daily batches (Gmail limit ≈ 500/day) · runs 9:00 AM Manila
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <Bar label="Invitations (bulk uploads)" used={inv.sent} cap={inv.cap} testId="batch-invitations">
          <div className="flex items-center justify-between gap-2">
            <span data-testid="batch-invitations-pending">Waiting: {inv.pending.member} members · {inv.pending.merchant} merchants</span>
            <Button size="sm" variant="ghost" className="text-[#00E5FF] h-7" onClick={sendNow} disabled={running || !inv.remaining} data-testid="batch-invitations-run-btn">
              {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}Send now
            </Button>
          </div>
        </Bar>
        <Bar label="Reminders (digest + follow-ups)" used={rem.sent} cap={rem.cap} testId="batch-reminders">
          <span data-testid="batch-reminders-pending">Follow-ups waiting: {fp.member_profile ?? 0} members · {fp.merchant_profile ?? 0} merchant profiles · {fp.merchant_services ?? 0} services</span>
        </Bar>
      </div>
    </div>
  );
}
